import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import SEOHead from './SEOHead';
import { 
  Crown, 
  Calendar, 
  User, 
  Clock, 
  ArrowLeft, 
  ArrowRight,
  Tag,
  Eye,
  BookOpen,
  Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DOMAIN = 'https://www.kingofit.ca';

// Blog List Page
const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [page, selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/blog/posts?page=${page}&limit=9`;
      if (selectedCategory) {
        url += `&category=${encodeURIComponent(selectedCategory)}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setPosts(data.posts);
      setTotalPages(data.pages);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      const data = await response.json();
      setCategories(data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <SEOHead
        title="IT Knowledge Center | Toronto Business IT Guides | King of IT"
        description="Expert IT guides for Toronto businesses: Microsoft 365, cybersecurity, managed IT, cloud migration. Practical answers to real business technology questions."
        keywords="IT knowledge base Toronto, Microsoft 365 guides, cybersecurity tips business, managed IT advice"
        path="/blog"
      />

      {/* Hero Section */}
      <section className="bg-gradient-hero text-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-4">
            <BookOpen className="w-4 h-4 mr-2" />
            IT Knowledge Center
          </Badge>
          <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-4">
            Practical IT Guides for <span className="text-gradient-gold">Toronto Businesses</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-8">
            Expert advice on Microsoft 365, cybersecurity, managed IT, and more. 
            Written by King of IT's team for small and mid-sized businesses.
          </p>
          
          {/* Quick Links to Answer Pages */}
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/answers/how-much-does-managed-it-cost-toronto">
              <Badge variant="outline" className="border-white/30 text-white hover:bg-white/10 cursor-pointer px-4 py-2">
                How much does managed IT cost?
              </Badge>
            </Link>
            <Link to="/answers/microsoft-365-business-premium-worth-it">
              <Badge variant="outline" className="border-white/30 text-white hover:bg-white/10 cursor-pointer px-4 py-2">
                Is M365 Business Premium worth it?
              </Badge>
            </Link>
            <Link to="/answers/small-business-cybersecurity-cost">
              <Badge variant="outline" className="border-white/30 text-white hover:bg-white/10 cursor-pointer px-4 py-2">
                Cybersecurity costs for SMBs
              </Badge>
            </Link>
            <Link to="/answers">
              <Badge className="bg-primary/20 text-primary border-primary/30 cursor-pointer px-4 py-2">
                View All Answers →
              </Badge>
            </Link>
          </div>
        </div>
      </section>

      {/* Knowledge Topics Quick Access */}
      <section className="bg-background py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/answers/microsoft-365-business-premium-worth-it" className="group">
              <Card className="p-4 hover:shadow-md transition-all hover:border-primary/50">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">Microsoft 365</h3>
                <p className="text-muted-foreground text-xs mt-1">Business Premium, SharePoint, Teams, security settings</p>
              </Card>
            </Link>
            <Link to="/answers/small-business-cybersecurity-cost" className="group">
              <Card className="p-4 hover:shadow-md transition-all hover:border-primary/50">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">Cybersecurity</h3>
                <p className="text-muted-foreground text-xs mt-1">Ransomware, phishing, endpoint protection, compliance</p>
              </Card>
            </Link>
            <Link to="/answers/how-much-does-managed-it-cost-toronto" className="group">
              <Card className="p-4 hover:shadow-md transition-all hover:border-primary/50">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">Managed IT</h3>
                <p className="text-muted-foreground text-xs mt-1">MSP pricing, what's included, break-fix vs managed</p>
              </Card>
            </Link>
            <Link to="/case-studies" className="group">
              <Card className="p-4 hover:shadow-md transition-all hover:border-primary/50">
                <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-sm">Case Studies</h3>
                <p className="text-muted-foreground text-xs mt-1">Real success stories from Toronto businesses</p>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-muted/30 py-6 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">Filter by:</span>
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => { setSelectedCategory(null); setPage(1); }}
              className={selectedCategory === null ? "bg-primary" : ""}
            >
              All Posts
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                size="sm"
                onClick={() => { setSelectedCategory(category); setPage(1); }}
                className={selectedCategory === category ? "bg-primary" : ""}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Posts Yet</h3>
              <p className="text-muted-foreground">Check back soon for new content!</p>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <Link to={`/blog/${post.slug}`} key={post.id}>
                    <Card className="h-full overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group">
                      {post.featured_image && (
                        <div className="h-48 overflow-hidden">
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      )}
                      <div className="p-6">
                        <Badge variant="outline" className="mb-3 text-xs">
                          {post.category}
                        </Badge>
                        <h2 className="font-display font-semibold text-lg text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            <span>{post.author}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            <span>{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-12">
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                  <span className="text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-navy text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Crown className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl sm:text-3xl mb-4">
            Need IT Support for Your Business?
          </h2>
          <p className="text-gray-300 mb-6">
            Get expert advice from Toronto's trusted Managed Service Provider.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gradient-gold text-primary-foreground font-semibold shadow-gold hover:opacity-90">
              Book a Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

// Single Blog Post Page
const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);

  useEffect(() => {
    fetchPost();
    fetchRecentPosts();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/blog/posts/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else {
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    }
    setLoading(false);
  };

  const fetchRecentPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/recent?limit=3`);
      const data = await response.json();
      setRecentPosts(data.posts);
    } catch (error) {
      console.error('Error fetching recent posts:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!post) return null;

  // Format date for schema (ISO 8601)
  const isoDate = (dateString) => {
    try {
      return new Date(dateString).toISOString();
    } catch {
      return new Date().toISOString();
    }
  };

  // Article/BlogPosting JSON-LD Schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.featured_image || `${DOMAIN}/og-image.png`,
    "datePublished": isoDate(post.created_at),
    "dateModified": isoDate(post.updated_at || post.created_at),
    "author": {
      "@type": "Person",
      "name": post.author,
      "url": `${DOMAIN}/about`
    },
    "publisher": {
      "@type": "Organization",
      "name": "King of IT",
      "logo": {
        "@type": "ImageObject",
        "url": `${DOMAIN}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${DOMAIN}/blog/${slug}`
    },
    "url": `${DOMAIN}/blog/${slug}`,
    "articleSection": post.category,
    "keywords": post.tags ? post.tags.join(", ") : post.category,
    "wordCount": post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0,
    "inLanguage": "en-CA"
  };

  // BreadcrumbList Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": DOMAIN
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": `${DOMAIN}/blog`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `${DOMAIN}/blog/${slug}`
      }
    ]
  };

  // HowTo Schema for blog posts with step-by-step instructions
  // Detects if the content has numbered lists or "step" keywords
  const generateHowToSchema = () => {
    const content = post.content || '';
    const hasSteps = /step\s*\d|^\d+\.\s|how\s+to|guide|tutorial/i.test(content);
    
    if (!hasSteps) return null;
    
    // Extract steps from content (looks for numbered lists or step patterns)
    const stepPattern = /(?:step\s*\d+[:\.\s]*|^\d+[\.\)]\s*)([^\n]+)/gim;
    const matches = [...content.matchAll(stepPattern)];
    
    if (matches.length < 2) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "HowTo",
      "name": post.title,
      "description": post.excerpt,
      "image": post.featured_image || `${DOMAIN}/og-image.png`,
      "totalTime": "PT30M",
      "step": matches.slice(0, 10).map((match, index) => ({
        "@type": "HowToStep",
        "position": index + 1,
        "name": `Step ${index + 1}`,
        "text": match[1].trim().replace(/<[^>]*>/g, '')
      }))
    };
  };

  const howToSchema = generateHowToSchema();

  // Speakable Schema for voice assistants
  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": post.title,
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", ".blog-excerpt", ".blog-content p:first-of-type"]
    },
    "url": `${DOMAIN}/blog/${slug}`
  };

  return (
    <>
      <SEOHead
        title={`${post.title} | King of IT Blog`}
        description={post.excerpt}
        path={`/blog/${slug}`}
        ogType="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(speakableSchema)}</script>
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      {/* Header */}
      <section className="bg-gradient-hero text-white py-12 lg:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-gray-300 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>
          <Badge className="bg-primary/20 text-primary border-primary/30 mb-4">
            {post.category}
          </Badge>
          <h1 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mb-4">
            {post.title}
          </h1>
          <p className="blog-excerpt text-gray-300 text-lg mb-4">{post.excerpt}</p>
          <div className="flex flex-wrap items-center gap-4 text-gray-300 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span>{post.views} views</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 lg:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <article className="blog-content prose prose-lg max-w-none">
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    h2: ({node, ...props}) => <h2 className="blog-h2" {...props} />,
                    h3: ({node, ...props}) => <h3 className="blog-h3" {...props} />,
                    h4: ({node, ...props}) => <h4 className="blog-h4" {...props} />,
                    p: ({node, ...props}) => <p className="blog-p" {...props} />,
                    ul: ({node, ...props}) => <ul className="blog-ul" {...props} />,
                    ol: ({node, ...props}) => <ol className="blog-ol" {...props} />,
                    li: ({node, ...props}) => <li className="blog-li" {...props} />,
                    strong: ({node, ...props}) => <strong className="blog-strong" {...props} />,
                    a: ({node, ...props}) => <a className="blog-link" {...props} />,
                    blockquote: ({node, ...props}) => <blockquote className="blog-blockquote" {...props} />,
                    hr: ({node, ...props}) => <hr className="blog-hr" {...props} />,
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </article>
              <style>{`
                .blog-content {
                  color: #374151;
                  line-height: 1.8;
                }
                .blog-content .blog-h2,
                .blog-content h2 {
                  font-size: 1.75rem;
                  font-weight: 700;
                  color: #1e3a5f;
                  margin-top: 2.5rem;
                  margin-bottom: 1rem;
                  padding-bottom: 0.5rem;
                  border-bottom: 2px solid #ffd700;
                }
                .blog-content .blog-h3,
                .blog-content h3 {
                  font-size: 1.35rem;
                  font-weight: 600;
                  color: #1e3a5f;
                  margin-top: 2rem;
                  margin-bottom: 0.75rem;
                }
                .blog-content .blog-h4,
                .blog-content h4 {
                  font-size: 1.2rem;
                  font-weight: 600;
                  color: #1e3a5f;
                  margin-top: 1.5rem;
                  margin-bottom: 0.5rem;
                }
                .blog-content .blog-p,
                .blog-content p {
                  margin-bottom: 1.25rem;
                  font-size: 1.1rem;
                }
                .blog-content .blog-ul,
                .blog-content ul {
                  margin-bottom: 1.5rem;
                  padding-left: 1.5rem;
                  list-style-type: disc;
                }
                .blog-content .blog-ol,
                .blog-content ol {
                  margin-bottom: 1.5rem;
                  padding-left: 1.5rem;
                  list-style-type: decimal;
                }
                .blog-content .blog-li,
                .blog-content li {
                  margin-bottom: 0.5rem;
                  font-size: 1.05rem;
                }
                .blog-content .blog-strong,
                .blog-content strong {
                  font-weight: 700;
                  color: #1f2937;
                }
                .blog-content .blog-link,
                .blog-content a {
                  color: #d4a00a;
                  text-decoration: underline;
                  font-weight: 500;
                }
                .blog-content .blog-link:hover,
                .blog-content a:hover {
                  color: #1e3a5f;
                }
                .blog-content .blog-blockquote,
                .blog-content blockquote {
                  border-left: 4px solid #ffd700;
                  padding-left: 1rem;
                  margin: 1.5rem 0;
                  font-style: italic;
                  background: #f9fafb;
                  padding: 1rem 1.5rem;
                  border-radius: 0 8px 8px 0;
                }
                .blog-content .blog-hr,
                .blog-content hr {
                  border: none;
                  border-top: 2px solid #e5e7eb;
                  margin: 2rem 0;
                }
              `}</style>
              
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="mt-8 pt-8 border-t">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Tag className="w-4 h-4 text-muted-foreground" />
                    {post.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Box */}
              <div className="mt-8 p-6 bg-muted/50 rounded-xl">
                <Link to="/author/alex-dayan" className="flex items-center gap-4 group">
                  <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center">
                    <Crown className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">{post.author}</h3>
                    <p className="text-sm text-muted-foreground">Owner & IT Consultant at King of IT</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      31 years of experience helping Toronto businesses with their IT needs.
                    </p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* CTA Card */}
              <Card className="p-6 bg-gradient-navy text-white mb-8">
                <Crown className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-display font-semibold text-lg mb-2">Need IT Help?</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Get a free consultation with our IT experts.
                </p>
                <Link to="/contact">
                  <Button className="w-full bg-gradient-gold text-primary-foreground font-semibold">
                    Book Free Consultation
                  </Button>
                </Link>
              </Card>

              {/* Recent Posts */}
              {recentPosts.length > 0 && (
                <Card className="p-6">
                  <h3 className="font-display font-semibold text-lg mb-4">Recent Posts</h3>
                  <div className="space-y-4">
                    {recentPosts.filter(p => p.slug !== slug).slice(0, 3).map((recentPost) => (
                      <Link 
                        key={recentPost.id} 
                        to={`/blog/${recentPost.slug}`}
                        className="block group"
                      >
                        <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
                          {recentPost.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(recentPost.created_at)}
                        </p>
                      </Link>
                    ))}
                  </div>
                </Card>
              )}
            </aside>
          </div>
        </div>
      </section>
    </>
  );
};

// Main Blog Page Component
const BlogPage = () => {
  const { slug } = useParams();
  
  if (slug) {
    return <BlogPost />;
  }
  
  return <BlogList />;
};

export default BlogPage;
